/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { convertForDataTest, EMPTY_STRING, isNotUndefined } from '@common/tech-shared';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { cva, VariantProps } from 'class-variance-authority';
import { uniqueId } from 'lodash-es';

export const textInputVariants = cva(
  [
    'w-full box-border rounded-lg border bg-background-50 px-3 py-2 text-base text-text leading-5',
    'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 placeholder:text-placeholder',
  ],
  {
    variants: {
      variant: {
        default: 'border-primary/50 focus-visible:outline-focus focus-visible:border-background-200 hover:border-primary-hover',
        error: 'border-error/50 hover:border-error focus-visible:outline-error focus-visible:border-background-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);
export type TextInputVariants = VariantProps<typeof textInputVariants>;

@Component({
  selector: 'ods-text-input',
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <label [for]="id" class="text-md text-text mb-2 block font-medium" [ngClass]="!showLabel && 'sr-only'">
      {{ inputLabel }}
      @if (required) {
        <i aria-hidden="true">*</i>
      }
    </label>

    @if (withPrefix) {
      <div class="pointer-events-none absolute bottom-2 left-2 flex size-6 items-center justify-center">
        <ng-content select="[prefix]" />
      </div>
    }
    <input
      type="text"
      [id]="id"
      [formControl]="fieldControl"
      [ngClass]="[textInputVariants({ variant }), withPrefix ? 'pl-10' : '', withSuffix ? 'pr-10' : '']"
      [placeholder]="placeholder"
      [autocomplete]="autocomplete"
      [attr.aria-required]="required"
      [attr.aria-invalid]="variant === 'error'"
      [attr.data-test-id]="_dataTestId + '-text-input'"
      [attr.aria-describedby]="ariaDescribedBy"
      [attr.aria-controls]="ariaControls"
      [attr.aria-expanded]="ariaExpanded"
      [attr.aria-autocomplete]="ariaAutocomplete"
      [attr.role]="ariaRole"
      (click)="clicked.emit($event)"
      #inputElement
    />
    @if (withSuffix) {
      <div class="absolute right-2 bottom-2 flex size-6 items-center justify-center">
        <ng-content select="[suffix]" />
      </div>
    }

    <ng-content select="[error]" />
  `,
  host: { class: 'block relative' },
})
export class TextInputComponent implements AfterViewInit {
  @Input({ required: true }) set label(label: string) {
    this.inputLabel = label;
    this.id = uniqueId(convertForDataTest(label) + '-');
    if (!this._dataTestId) this._dataTestId = convertForDataTest(this.inputLabel);
  }
  @Input() placeholder: string = '';
  @Input() autocomplete: string = 'off';
  @Input() variant: TextInputVariants['variant'];
  @Input() fieldControl: FormControl = new FormControl(EMPTY_STRING);
  @Input() required: boolean = false;
  @Input() withPrefix: boolean = false;
  @Input() withSuffix: boolean = false;
  @Input() showLabel: boolean = true;
  @Input() focus: boolean = false;
  @Input() set dataTestId(value: string) {
    if (isNotUndefined(value)) this._dataTestId = value;
  }
  @Input() ariaDescribedBy: string = null;
  @Input() ariaRole: string = null;
  @Input() ariaControls: string = null;
  @Input() ariaExpanded: boolean = null;
  @Input() ariaAutocomplete: 'none' | 'inline' | 'list' | 'both' = 'none';

  @Output() clicked: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  @ViewChild('inputElement') inputElement: ElementRef;

  public inputLabel: string;
  public id: string;
  public _dataTestId: string;
  public textInputVariants = textInputVariants;

  ngAfterViewInit() {
    if (this.focus) this.focusInput();
  }

  focusInput(): void {
    setTimeout(() => this.inputElement.nativeElement.focus(), 0);
  }
}
